import type { ColorKey } from '@iheartjane/reefer';
import {
  Box,
  Flex,
  Image,
  JaneGoldIcon,
  Link,
  Typography,
} from '@iheartjane/reefer';
import { useInView } from 'react-intersection-observer';

import styles from './searchSuggestions.module.css';

interface BoldReverseMatchProps {
  className?: string;
  color?: ColorKey;
  matcher: string;
  maxLines?: number | 'none';
  stringToMatch: string | null;
}

const BoldReverseMatch = ({
  className,
  color = 'text-light',
  matcher,
  maxLines = 'none',
  stringToMatch,
}: BoldReverseMatchProps) => {
  if (!stringToMatch) return null;
  const sanitized = matcher.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${sanitized})`, 'gi');
  const parts = stringToMatch.split(regex);

  return (
    <Typography
      aria-label={stringToMatch}
      color={color}
      className={className}
      maxLines={maxLines}
    >
      {parts.map((part, index) =>
        regex.test(part) ? (
          <mark key={index} className={styles['mark']}>
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </Typography>
  );
};

export interface SearchSuggestionProductCardProps {
  baseImage: string;
  brand: string | null;
  imageSrcSet?: { sizes: string; srcSet: string };
  kind: string;
  lineage: string;
  name: string;
  onClick?: () => void;
  onImpress?: () => void;
  query: string;
  showGoldBadge: boolean;
  specialLabel: string | null;
  to: string;
}

export function SearchSuggestionProductCard({
  baseImage,
  brand,
  imageSrcSet,
  kind,
  lineage,
  name,
  onClick,
  onImpress,
  query,
  showGoldBadge,
  specialLabel,
  to,
}: SearchSuggestionProductCardProps) {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        onImpress?.();
      }
    },
  });

  return (
    <Link
      className={styles['card']}
      variant="minimal"
      to={to}
      onClick={onClick}
    >
      <Flex gap={16}>
        <Box ref={ref} position="relative">
          <Flex
            className={styles['badgesContainer']}
            justifyContent="center"
            gap={2}
            p={8}
            position="absolute"
            left={0}
            right={0}
          >
            {showGoldBadge && <JaneGoldIcon />}
            {specialLabel && (
              <Box className={styles['specialBadge']}>{specialLabel}</Box>
            )}
          </Flex>
          <Image
            src={baseImage}
            altText={name}
            border
            loading="eager"
            objectFit="cover"
            srcSet={imageSrcSet?.srcSet}
            sizes={imageSrcSet?.sizes}
            height="88px"
            width="88px"
            borderRadius="8px"
          />
        </Box>
        <Box overflow="hidden">
          <BoldReverseMatch
            stringToMatch={name}
            matcher={query}
            color="text-main"
            className={styles['name']}
            maxLines={1}
          />
          <BoldReverseMatch stringToMatch={brand} matcher={query} />
          <BoldReverseMatch
            stringToMatch={`${lineage} ${kind}`}
            matcher={query}
          />
        </Box>
      </Flex>
    </Link>
  );
}
